import { createContext, useContext, useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';

const RegionContext = createContext();

export const useRegion = () => {
  const context = useContext(RegionContext);
  if (!context) {
    throw new Error('useRegion must be used within a RegionProvider');
  }
  return context;
};

// Auto-detect region based on browser locale
const detectRegionFromBrowser = () => {
  if (typeof navigator === 'undefined') return 'us'; // Default to US if navigator unavailable
  
  const language = navigator.language || navigator.userLanguage || '';
  const languages = navigator.languages || [];
  
  // Check language preferences
  const allLangs = [language, ...languages].map(l => l.toLowerCase());
  
  // Check for Spanish (Spain)
  const isES = allLangs.some(lang => 
    lang === 'es-es' || lang === 'es' || lang.includes('-es')
  );
  
  // Check for UK
  const isUK = allLangs.some(lang => 
    lang === 'en-gb' || lang.includes('-gb') || lang.includes('-uk')
  );
  
  if (isES) return 'es';
  if (isUK) return 'uk';
  return 'us';
};

// Get initial region from localStorage or auto-detect (runs once at module load)
const getInitialRegion = () => {
  if (typeof window !== 'undefined') {
    const saved = localStorage.getItem('coolgirl-region');
    if (saved === 'uk' || saved === 'us' || saved === 'es') {
      return saved;
    }
    // Auto-detect and persist
    const detected = detectRegionFromBrowser();
    localStorage.setItem('coolgirl-region', detected);
    return detected;
  }
  return 'us'; // Server-side fallback
};

export const RegionProvider = ({ children }) => {
  const [region, setRegion] = useState(getInitialRegion);
  const isInitialMount = useRef(true);

  // Check for region change notification on mount
  useEffect(() => {
    const regionChanged = localStorage.getItem('coolgirl-region-changed');
    if (regionChanged) {
      localStorage.removeItem('coolgirl-region-changed');
      const regionLabels = { uk: '🇬🇧 UK', us: '🇺🇸 US', es: '🇪🇸 Spain' };
      toast.success(`Now showing ${regionLabels[region] || region} products`, {
        duration: 3000,
        position: 'bottom-center',
      });
    }
  }, [region]);

  // Save region to localStorage when it changes (but not on initial mount)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    localStorage.setItem('coolgirl-region', region);
  }, [region]);

  const toggleRegion = () => {
    // Cycle through regions: us -> uk -> es -> us
    const nextRegion = region === 'us' ? 'uk' : region === 'uk' ? 'es' : 'us';
    localStorage.setItem('coolgirl-region', nextRegion);
    localStorage.setItem('coolgirl-region-changed', 'true');
    window.location.reload();
  };

  const setRegionTo = (newRegion) => {
    if (['uk', 'us', 'es'].includes(newRegion) && newRegion !== region) {
      localStorage.setItem('coolgirl-region', newRegion);
      localStorage.setItem('coolgirl-region-changed', 'true');
      window.location.reload();
    }
  };

  // Get currency and Amazon domain based on region
  const getCurrencySymbol = () => {
    switch (region) {
      case 'uk': return '£';
      case 'es': return '€';
      default: return '$';
    }
  };

  const getAmazonDomain = () => {
    switch (region) {
      case 'uk': return 'amazon.co.uk';
      case 'es': return 'amazon.es';
      default: return 'amazon.com';
    }
  };

  return (
    <RegionContext.Provider
      value={{
        region,
        isUK: region === 'uk',
        isUS: region === 'us',
        isES: region === 'es',
        toggleRegion,
        setRegionTo,
        currencySymbol: getCurrencySymbol(),
        amazonDomain: getAmazonDomain(),
      }}
    >
      {children}
    </RegionContext.Provider>
  );
};
